import { useState, type FormEvent } from 'react';
import { dateDuJour, formaterIdentite } from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { api, ErreurApi } from '../api/client';
import { BandeauAlerte } from './BandeauAlerte';
import { Champ } from './Champ';
import { Modale } from './Modale';

interface Profil {
  id: string;
  libelle: string;
}
interface Agence {
  id: string;
  code: string;
  libelle: string;
}
interface ClientListe {
  id: string;
  reference: string;
  nom: string;
  prenom: string;
  mandats: { statut: string }[];
}

export interface ClientDuMandat {
  id: string;
  reference: string;
}

/** Statuts qui libèrent le client : au-delà, la RG-01 interdit un second mandat. */
const STATUTS_TERMINAUX = ['CLOTURE', 'ANNULE'];

export function aUnMandatVivant(client: { mandats: { statut: string }[] }) {
  return client.mandats.some((m) => !STATUTS_TERMINAUX.includes(m.statut));
}

/**
 * Enregistrement d'un mandat, ouvert depuis la fiche client, la liste des
 * clients ou la liste des mandats. Sans `client`, la modale propose de le
 * choisir parmi ceux qui n'ont pas de mandat vivant (RG-01).
 */
export function ModaleMandat({
  ouverte,
  onFermer,
  onCree,
  client,
}: {
  ouverte: boolean;
  onFermer: () => void;
  onCree: (mandat: { id: string }) => void | Promise<void>;
  client?: ClientDuMandat;
}) {
  return (
    <Modale
      ouverte={ouverte}
      onFermer={onFermer}
      surtitre={client ? `Mandat · ${client.reference}` : 'Mandat'}
      titre="Enregistrer un mandat"
      taille="large"
    >
      {/* La modale démonte son contenu à la fermeture : la saisie repart à
          vide à chaque ouverture, et les référentiels ne se chargent qu'à ce
          moment-là. */}
      <FormulaireMandat client={client} onFermer={onFermer} onCree={onCree} />
    </Modale>
  );
}

function FormulaireMandat({
  client,
  onFermer,
  onCree,
}: {
  client?: ClientDuMandat;
  onFermer: () => void;
  onCree: (mandat: { id: string }) => void | Promise<void>;
}) {
  const profils = useRessource<Profil[]>('/profils');
  const agences = useRessource<Agence[]>('/agences');
  const clients = useRessource<ClientListe[]>(client ? null : '/clients');
  const clientsLibres = (clients.donnees ?? []).filter((c) => !aUnMandatVivant(c));

  const [saisie, setSaisie] = useState({
    clientId: client?.id ?? '',
    agenceId: '',
    profilId: '',
    profilDateDetermination: dateDuJour(),
    profilReferenceDossier: '',
    dateSignature: dateDuJour(),
    ibanCompteEspece: '',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const champ = (cle: keyof typeof saisie) => ({
    value: saisie[cle],
    onChange: (e: { target: { value: string } }) => setSaisie({ ...saisie, [cle]: e.target.value }),
  });

  async function creer(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setOccupe(true);
    try {
      const mandat = await api.post<{ id: string }>('/mandats', {
        clientId: saisie.clientId,
        agenceId: saisie.agenceId,
        profilId: saisie.profilId,
        profilDateDetermination: saisie.profilDateDetermination,
        profilReferenceDossier: saisie.profilReferenceDossier || undefined,
        dateSignature: saisie.dateSignature || undefined,
        ibanCompteEspece: saisie.ibanCompteEspece || undefined,
      });
      await onCree(mandat);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? messageErreur(e) : 'Enregistrement impossible.');
    } finally {
      setOccupe(false);
    }
  }

  return (
    <>
      {erreur && (
        <BandeauAlerte nature="refus" titre="Enregistrement refusé">
          {erreur}
        </BandeauAlerte>
      )}
      <p className="consigne consigne--tete">
        Éléments reçus de l’agence : profil de risque déjà déterminé et RIB.
      </p>
      <form onSubmit={(e) => void creer(e)} noValidate>
        {!client && (
          <div className="champ">
            <label className="surtitre" htmlFor="mandat-client">
              Client
            </label>
            <select
              id="mandat-client"
              className="champ__select"
              value={saisie.clientId}
              onChange={(e) => setSaisie({ ...saisie, clientId: e.target.value })}
              required
            >
              <option value="">{clients.chargement ? 'Chargement…' : '—'}</option>
              {clientsLibres.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.reference} — {formaterIdentite(c.nom, c.prenom)}
                </option>
              ))}
            </select>
            <span className="champ__aide">
              {!clients.chargement && clientsLibres.length === 0
                ? 'Aucun client sans mandat. Créez d’abord la fiche dans Clients.'
                : 'Seuls les clients sans mandat en cours sont proposés : un client n’en détient qu’un.'}
            </span>
          </div>
        )}

        <div className="champs-en-ligne">
          <div className="champ">
            <label className="surtitre" htmlFor="mandat-agence">
              Agence de rattachement
            </label>
            <select
              id="mandat-agence"
              className="champ__select"
              value={saisie.agenceId}
              onChange={(e) => setSaisie({ ...saisie, agenceId: e.target.value })}
              required
            >
              <option value="">—</option>
              {(agences.donnees ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.libelle}
                </option>
              ))}
            </select>
          </div>

          <div className="champ">
            <label className="surtitre" htmlFor="mandat-profil">
              Profil de risque (déterminé en agence)
            </label>
            <select
              id="mandat-profil"
              className="champ__select"
              value={saisie.profilId}
              onChange={(e) => setSaisie({ ...saisie, profilId: e.target.value })}
              required
            >
              <option value="">—</option>
              {(profils.donnees ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.libelle}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="champs-en-ligne">
          <Champ
            label="Date de détermination du profil"
            type="date"
            {...champ('profilDateDetermination')}
            aide="Date à laquelle l’agence a arrêté le profil."
            required
          />
          <Champ label="Référence du dossier de profilage" {...champ('profilReferenceDossier')} />
        </div>
        <div className="champs-en-ligne">
          <Champ label="Date de signature du mandat" type="date" {...champ('dateSignature')} />
          <Champ label="RIB / IBAN du compte espèces" {...champ('ibanCompteEspece')} />
        </div>

        <div className="modale__actions">
          <button type="button" className="bouton bouton--fantome" onClick={onFermer}>
            Annuler
          </button>
          <button
            className="bouton"
            type="submit"
            disabled={
              occupe ||
              !saisie.clientId ||
              !saisie.agenceId ||
              !saisie.profilId ||
              !saisie.profilDateDetermination
            }
          >
            {occupe ? 'Enregistrement…' : 'Enregistrer le mandat'}
          </button>
        </div>
      </form>
    </>
  );
}
